(() => {
  const SELECTOR_PICKER = 'custom-size-picker';
  const SELECTOR_CUSTOM_OPTION = '[data-custom-size-option="true"]';

  const normalise = value => String(value || '').trim().toLowerCase();

  const getProductInfo = sectionId =>
    document.querySelector(`product-info[data-section="${CSS.escape(sectionId)}"]`);

  const getActiveVariantSelector = productInfo => {
    const selectors = [...productInfo.querySelectorAll('product-variant-selects')];
    return selectors.find(selector => selector.style.display !== 'none') || selectors[0];
  };

  const selectedVariantUsesCustomSize = picker => {
    const productInfo = picker.closest('product-info');
    const selector = productInfo && getActiveVariantSelector(productInfo);
    const variantJson = selector?.querySelector('[data-selected-variant]')?.textContent?.trim();
    if (!variantJson) return false;

    try {
      const variant = JSON.parse(variantJson);
      const customValue = normalise(picker.dataset.customSizeValue);
      return variant?.options?.some(value => normalise(value) === customValue);
    } catch (_error) {
      return false;
    }
  };

  const openPicker = picker => {
    const details = picker.querySelector('details');
    if (!details || details.hasAttribute('open')) return;
    picker.querySelector('.custom-size-picker__open')?.click();
  };

  const setVirtualCustomSizeActive = (productInfo, active) => {
    const picker = productInfo?.querySelector(SELECTOR_PICKER);
    const button = productInfo?.querySelector('[data-custom-size-virtual]');
    if (!picker || !button) return;

    picker.dataset.customSizeVirtualActive = active ? 'true' : 'false';
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');

    if (active) {
      const selectedValue = button.closest('.product-form__input')?.querySelector('[data-selected-value]');
      if (selectedValue) selectedValue.textContent = picker.dataset.customSizeValue;
    } else {
      const optionContainer = button.closest('.product-form__input');
      const selectedValue = optionContainer?.querySelector('[data-selected-value]');
      const checkedOption = optionContainer?.querySelector('input[type="radio"]:checked');
      if (selectedValue && checkedOption) selectedValue.textContent = checkedOption.value;
    }
  };

  const openPickerWhenVariantIsReady = sectionId => {
    let attempts = 0;
    const maxAttempts = 60;

    const check = () => {
      const productInfo = getProductInfo(sectionId);
      const picker = productInfo?.querySelector(SELECTOR_PICKER);

      if (picker && selectedVariantUsesCustomSize(picker)) {
        openPicker(picker);
        return;
      }

      attempts += 1;
      if (attempts < maxAttempts) window.setTimeout(check, 100);
    };

    window.setTimeout(check, 0);
  };

  const setPropertiesActive = (picker, active) => {
    picker.querySelectorAll('[data-custom-size-field]').forEach(field => {
      if (active) {
        field.name = field.dataset.propertyName;
      } else {
        field.removeAttribute('name');
      }
    });
    picker.dataset.customSizeApplied = active ? 'true' : 'false';
  };

  const showError = (picker, message) => {
    const error = picker.querySelector('[data-custom-size-error]');
    if (!error) return;
    error.textContent = message || picker.dataset.errorMessage;
    error.hidden = false;
  };

  const clearError = picker => {
    const error = picker.querySelector('[data-custom-size-error]');
    if (error) error.hidden = true;
  };

  // The measurement inputs stay numeric, so the unit lives separately: it is
  // mirrored into the suffix beside each field and into the hidden property that
  // travels with the order.
  const syncUnits = picker => {
    if (!picker) return;
    const checked = picker.querySelector('[data-custom-size-unit]:checked');
    if (!checked) return;

    const unit = checked.value;
    picker.querySelectorAll('[data-custom-size-unit-suffix]').forEach(suffix => {
      suffix.textContent = unit;
    });
    const hidden = picker.querySelector('[data-custom-size-unit-value]');
    if (hidden) hidden.value = unit;
  };

  const resetPicker = picker => {
    picker.querySelectorAll('input[data-custom-size-field], textarea[data-custom-size-field]').forEach(field => {
      if (field.type !== 'hidden') field.value = '';
      field.removeAttribute('aria-invalid');
    });
    const counter = picker.querySelector('[data-custom-size-count]');
    if (counter) counter.textContent = '0';
    // Reset returns the unit to the theme default rather than clearing it, so
    // the suffix and the hidden property always carry a real value.
    const defaultUnit = picker.querySelector('[data-custom-size-unit][checked]');
    if (defaultUnit) defaultUnit.checked = true;
    syncUnits(picker);
    clearError(picker);
    setPropertiesActive(picker, false);
  };

  const applyPicker = picker => {
    const requiredFields = [...picker.querySelectorAll('[data-custom-size-field][data-required="true"]')];
    const invalidFields = requiredFields.filter(field => !field.value.trim());

    requiredFields.forEach(field => {
      if (invalidFields.includes(field)) {
        field.setAttribute('aria-invalid', 'true');
      } else {
        field.removeAttribute('aria-invalid');
      }
    });

    if (invalidFields.length) {
      showError(picker);
      invalidFields[0].focus();
      return false;
    }

    clearError(picker);
    setPropertiesActive(picker, true);
    return true;
  };

  const closePicker = picker => {
    const details = picker?.querySelector('details');
    if (!details || !details.hasAttribute('open')) return;
    picker.querySelector('.js-btn-close-drawer')?.click();
  };

  // Apply attaches the measurements as line-item properties and then submits the
  // product form, so one click both saves the size and adds the item to the cart.
  const submitProductForm = productInfo => {
    const form = productInfo?.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) return;

    const submitButton =
      form.querySelector('[type="submit"]:not([disabled])') ||
      productInfo.querySelector('.product-form__submit:not([disabled])');

    if (!submitButton || submitButton.getAttribute('aria-disabled') === 'true') return;

    // Click the button rather than form.submit() so product-form's submit handler
    // runs (it intercepts, posts to /cart/add and opens the cart drawer).
    submitButton.click();
  };

  const customOptionIsSelected = productInfo => {
    const picker = productInfo.querySelector(SELECTOR_PICKER);
    if (picker?.dataset.customSizeVirtualActive === 'true') return true;

    const selector = getActiveVariantSelector(productInfo);
    if (!selector) return false;

    const checkedCustomOption = selector.querySelector(`input${SELECTOR_CUSTOM_OPTION}:checked`);
    if (checkedCustomOption) return true;

    const selectedOption = selector.querySelector(`select option${SELECTOR_CUSTOM_OPTION}:checked`);
    return Boolean(selectedOption);
  };

  // product-variant-selects is replaced wholesale by HTMLUpdateUtility.viewTransition
  // on every variant change, which destroys the drawer the customer just filled in.
  // Snapshot the values before the swap and restore them into the fresh picker so
  // measurements survive selecting a different colour/fabric.
  const pendingState = new WeakMap();

  const readPickerState = picker => {
    if (!picker) return null;
    const values = {};
    picker.querySelectorAll('[data-custom-size-field]').forEach(field => {
      if (field.type === 'hidden') return;
      values[field.dataset.propertyName] = field.value;
    });
    return {
      values,
      unit: picker.querySelector('[data-custom-size-unit]:checked')?.value || null,
      applied: picker.dataset.customSizeApplied === 'true',
      virtualActive: picker.dataset.customSizeVirtualActive === 'true'
    };
  };

  const writePickerState = (productInfo, state) => {
    const picker = productInfo?.querySelector(SELECTOR_PICKER);
    if (!picker || !state) return;

    let restoredAny = false;
    picker.querySelectorAll('[data-custom-size-field]').forEach(field => {
      if (field.type === 'hidden') return;
      const value = state.values[field.dataset.propertyName];
      if (value == null || value === '') return;
      field.value = value;
      restoredAny = true;
    });

    if (state.unit) {
      const unitInput = [...picker.querySelectorAll('[data-custom-size-unit]')].find(
        input => input.value === state.unit
      );
      if (unitInput) unitInput.checked = true;
    }
    syncUnits(picker);

    const notes = picker.querySelector('[data-custom-size-notes]');
    const counter = picker.querySelector('[data-custom-size-count]');
    if (notes && counter) counter.textContent = String(notes.value.length);

    if (state.virtualActive) setVirtualCustomSizeActive(productInfo, true);
    if (state.applied && restoredAny) setPropertiesActive(picker, true);
  };

  const observeVariantSwaps = () => {
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType !== 1) return;
          if (!node.matches?.('product-variant-selects')) return;

          const productInfo = node.closest('product-info');
          const state = pendingState.get(productInfo);
          if (!productInfo || !state) return;

          pendingState.delete(productInfo);
          writePickerState(productInfo, state);
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  };

  // Capture state at the moment an option is clicked/changed -- the swap happens
  // asynchronously once the section request resolves.
  const stashState = productInfo => {
    if (!productInfo) return;
    const state = readPickerState(productInfo.querySelector(SELECTOR_PICKER));
    if (state) pendingState.set(productInfo, state);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeVariantSwaps);
  } else {
    observeVariantSwaps();
  }

  document.addEventListener('click', event => {
    const virtualButton = event.target.closest('[data-custom-size-virtual]');
    if (virtualButton) {
      event.preventDefault();
      const productInfo = virtualButton.closest('product-info');
      const picker = productInfo?.querySelector(SELECTOR_PICKER);
      if (!productInfo || !picker) return;

      if (picker.dataset.customSizeVirtualActive !== 'true') {
        resetPicker(picker);
        setVirtualCustomSizeActive(productInfo, true);
      }
      openPicker(picker);
      return;
    }

    // Labels inside the drawer point at measurement fields, not variant options.
    const label = event.target.closest(SELECTOR_PICKER) ? null : event.target.closest('label[for]');
    const option = label?.control;
    if (option?.matches(SELECTOR_CUSTOM_OPTION)) {
      const productInfo = option.closest('product-info');
      if (productInfo) {
        stashState(productInfo);
        openPickerWhenVariantIsReady(productInfo.dataset.section);
      }
      return;
    }

    if (option) {
      const productInfo = option.closest('product-info');
      const picker = productInfo?.querySelector(SELECTOR_PICKER);
      if (productInfo && picker?.dataset.customSizeVirtualActive === 'true') {
        resetPicker(picker);
        setVirtualCustomSizeActive(productInfo, false);
      } else {
        stashState(productInfo);
      }
    }

    const unitLabel = event.target.closest('.custom-size-picker__unit-label');
    if (unitLabel) {
      const picker = unitLabel.closest(SELECTOR_PICKER);
      // Let the radio update first, then mirror it and require a fresh Apply.
      requestAnimationFrame(() => {
        syncUnits(picker);
        clearError(picker);
        setPropertiesActive(picker, false);
      });
      return;
    }

    const resetButton = event.target.closest('[data-custom-size-reset]');
    if (resetButton) {
      event.preventDefault();
      resetPicker(resetButton.closest(SELECTOR_PICKER));
      return;
    }

    const applyButton = event.target.closest('[data-custom-size-apply]');
    if (applyButton) {
      event.preventDefault();
      const picker = applyButton.closest(SELECTOR_PICKER);
      if (!applyPicker(picker)) return;

      // Close first, then submit on the next frame: MenuDrawer resets
      // body.style.overflow on close, which would otherwise clobber the overflow
      // lock the cart drawer sets when it opens.
      const productInfo = picker.closest('product-info');
      closePicker(picker);
      requestAnimationFrame(() => submitProductForm(productInfo));
    }
  });

  document.addEventListener('change', event => {
    // Fields inside the drawer are not variant options -- never let them reset it.
    const insidePicker = event.target.closest(SELECTOR_PICKER);
    if (insidePicker) {
      if (event.target.matches('[data-custom-size-unit]')) syncUnits(insidePicker);
      return;
    }

    const select = event.target.closest('select');
    const customOption = select?.selectedOptions?.[0];
    const productInfo = select?.closest('product-info');
    if (customOption?.matches(SELECTOR_CUSTOM_OPTION)) {
      if (productInfo) {
        stashState(productInfo);
        openPickerWhenVariantIsReady(productInfo.dataset.section);
      }
      return;
    }

    const picker = productInfo?.querySelector(SELECTOR_PICKER);
    if (productInfo && picker?.dataset.customSizeVirtualActive === 'true') {
      resetPicker(picker);
      setVirtualCustomSizeActive(productInfo, false);
    } else if (select) {
      stashState(productInfo);
    }
  });

  document.addEventListener('input', event => {
    const field = event.target.closest('[data-custom-size-field]');
    if (!field) return;

    field.removeAttribute('aria-invalid');
    const picker = field.closest(SELECTOR_PICKER);
    clearError(picker);
    setPropertiesActive(picker, false);

    if (field.matches('[data-custom-size-notes]')) {
      const counter = picker.querySelector('[data-custom-size-count]');
      if (counter) counter.textContent = String(field.value.length);
    }
  });

  document.addEventListener('submit', event => {
    const form = event.target.closest('form[data-type="add-to-cart-form"]');
    const productInfo = form?.closest('product-info');
    if (!productInfo || !customOptionIsSelected(productInfo)) return;

    const picker = productInfo.querySelector(SELECTOR_PICKER);
    if (!picker || picker.dataset.customSizeApplied === 'true') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    showError(picker);
    openPicker(picker);
  }, true);
})();
